import React, { useState, useRef } from 'react';
import GroupsModal from '../Modal/GroupsModal';
import LoadingOverlay from '../components/LoadingOverlay';
import { extractPostDate } from '../utils/dateUtils';
import { CATEGORIAS_EMOJI, ESTILO_BANDEIRA } from '../utils/emojis';

// Component que encapsula todo o UI de postagem (short input + expanded composer)
export default function FormularioPostagem({ apiCall, apiUploadMultipart, onPosted, fixedGroup = null, hideGroupButton = false, autoExpand = false, hideCancelButton = false }) {
    const [expanded, setExpanded] = useState(autoExpand);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [uploadFiles, setUploadFiles] = useState([]);
    const [posting, setPosting] = useState(false);
    const [showFilesModal, setShowFilesModal] = useState(false);
    const [descriptionError, setDescriptionError] = useState('');
    const [groupFlags, setGroupFlags] = useState({ publicOnly: true, onlyGroups: false });

    const imageInputRef = useRef(null);
    const filesInputRef = useRef(null);
    const emojiRef = useRef(null);
    const [emojiOpen, setEmojiOpen] = useState(false);

    // Fecha o dropdown de emoji ao clicar fora do componente
    React.useEffect(() => {
        const onDocClick = (ev) => {
            try {
                if (!emojiOpen) return;
                const el = emojiRef.current;
                if (!el) return;
                if (!el.contains(ev.target)) {
                    setEmojiOpen(false);
                }
            } catch (e) {
                // ignore
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [emojiOpen]);
    const [emojiCategory, setEmojiCategory] = useState('faces');
    const [showMediaGrid, setShowMediaGrid] = useState(false);

    const [groupsOpen, setGroupsOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    // If fixedGroup prop provided, force the selection and disable changes
    const fixedGroupObj = fixedGroup || null;

    // initialize selection from fixedGroup when component mounts or when fixedGroup changes
    React.useEffect(() => {
        if (fixedGroupObj) {
            setSelectedGroup(fixedGroupObj);
            // force onlyGroups mode so post will go to that group
            setGroupFlags({ publicOnly: false, onlyGroups: true });
        }
    }, [fixedGroupObj]);

    // helper to get group name consistently
    const getGroupName = (g) => {
      if (!g) return '';
      return (g.NomeGrupo || g.nomeGrupo || g.Nome || g.nome || g.name || g.Grupo || g.GrupoNome || '') + '';
    };

    // compute display label for groups button (supports array or single)
    const groupLabel = (() => {
      const s = selectedGroup || fixedGroupObj;
      if (!s) return '+ Grupos';
      if (Array.isArray(s)) {
        if (s.length === 0) return '+ Grupos';
        if (s.length === 1) return getGroupName(s[0]) || '+ Grupos';
        return (getGroupName(s[0]) || 'Grupos') + ` +${s.length - 1}`;
      }
      return getGroupName(s) || '+ Grupos';
    })();


    const onCreateClick = () => setExpanded(true);

    // Utilities to get group id/name consistently even when fixedGroup is present
    const getFixedGroupId = (g) => {
        if (!g) return null;
        return g.Id || g.id || g.GrupoId || g.grupoId || null;
    };
    const getFixedGroupName = (g) => {
        if (!g) return '';
        return (g.NomeGrupo || g.nomeGrupo || g.Nome || g.nome || g.name || g.Grupo || g.GrupoNome || '') + '';
    };

    const toggleEmoji = (e) => { e.stopPropagation(); if (!expanded) setExpanded(true); setEmojiOpen(!emojiOpen); };

    const onFilesIconClick = () => { if (!expanded) setExpanded(true); if (filesInputRef.current) filesInputRef.current.click(); };
    const onImageIconClick = () => { if (!expanded) setExpanded(true); if (imageInputRef.current) imageInputRef.current.click(); };

    // If this form is fixed to a group, avoid showing the groups modal and disable group-changing actions
    const onGroupsClick = (e) => {
        e.stopPropagation();
        if (fixedGroupObj) return; // disabled when fixed
        setGroupsOpen(true);
        setEmojiOpen(false);
    };



    const handleFilesSelected = (e) => {
        const all = Array.from(e.target.files || []);
        if (!all.length) { e.target.value = ''; return; }
        const allowedExts = ['pdf','txt','csv','doc','docx','xls','xlsx','ppt','pptx'];
        const allowedTypes = [
            'application/pdf',
            'text/plain',
            'text/csv',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ];
        const valid = [];
        const invalidNames = [];
        all.forEach(f => {
            const name = (f.name || '').toLowerCase();
            const ext = name.includes('.') ? name.split('.').pop() : '';
            // explicitly disallow archives, images and video
            if ((f.type && (f.type.startsWith('image/') || f.type.startsWith('video/'))) || ext === 'zip' || ext === 'rar' || ext === '7z') {
                invalidNames.push(f.name);
                return;
            }
            if (allowedExts.includes(ext) || allowedTypes.includes(f.type)) {
                valid.push(f);
            } else {
                invalidNames.push(f.name);
            }
        });
        if (invalidNames.length) {
            alert('Alguns arquivos foram ignorados. Tipos permitidos: PDF, DOC/DOCX, TXT, CSV, XLS/XLSX, PPT/PPTX. Arquivos ignorados: ' + invalidNames.join(', '));
        }
        if (!valid.length) { e.target.value = ''; return; }
        setUploadFiles(prev => {
            const existing = new Set(prev.map(p => `${p.file.name}_${p.file.size}`));
            const add = [];
            valid.forEach(f => {
                const k = `${f.name}_${f.size}`;
                if (!existing.has(k)) add.push({ file: f, preview: null });
            });
            return [...prev, ...add];
        });
        e.target.value = '';
        if (!expanded) setExpanded(true);
    };

    const handleImagesSelected = async (e) => {
        const files = Array.from(e.target.files || []).filter(f => f.type && f.type.startsWith('image/'));
        if (!files.length) { e.target.value = ''; return; }
        const existingKeys = new Set(uploadFiles.map(p => `${p.file.name}_${p.file.size}`));
        const toAddPromises = files.map(async (f) => {
            const k = `${f.name}_${f.size}`;
            if (existingKeys.has(k)) return null;
            const preview = URL.createObjectURL(f);
            const orientation = await new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    const w = img.naturalWidth;
                    const h = img.naturalHeight;
                    const ratio = Math.abs(h - w) / Math.max(h, w, 1);
                    const isSquare = ratio < 0.05;
                    const isPortrait = !isSquare && h > w;
                    resolve({ isPortrait, isSquare });
                };
                img.onerror = () => { resolve({ isPortrait: false, isSquare: false }); };
                img.src = preview;
            });
            return { file: f, preview, isPortrait: orientation.isPortrait, isSquare: orientation.isSquare };
        });
        let adds = (await Promise.all(toAddPromises)).filter(Boolean);
        if (adds.length === 0) { e.target.value = ''; return; }

        // Enforce max 15 photos per postagem
        const currentImageCount = uploadFiles.filter(p => p.file && p.file.type && p.file.type.startsWith('image/')).length;
        const allowed = Math.max(0, 15 - currentImageCount);
        if (allowed <= 0) {
            alert('Limite de 15 imagens por postagem atingido.');
            e.target.value = '';
            return;
        }
        if (adds.length > allowed) {
            adds = adds.slice(0, allowed);
            alert(`Apenas ${allowed} imagem(s) foram adicionadas para não ultrapassar o limite de 15.`);
        }

        setUploadFiles(prev => [...adds, ...prev]);
        e.target.value = '';
        if (!expanded) setExpanded(true);
    };

    const openMediaGrid = () => { setShowMediaGrid(true); setTimeout(() => { const btn = document.querySelector('.media-grid-modal .modal-close-btn'); if (btn) btn.focus(); }, 50); };
    const closeMediaGrid = () => setShowMediaGrid(false);

    const onEmojiSelect = (emoji) => { if (!expanded) setExpanded(true); setDescription((d) => (d ? d + ' ' + emoji : emoji)); };

    // helper: convert File to base64 string (no data: prefix)
    const fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const res = reader.result;
            if (typeof res === 'string') {
                const parts = res.split(',');
                resolve(parts.length > 1 ? parts[1] : parts[0]);
            } else {
                const arr = new Uint8Array(res);
                let binary = '';
                for (let i = 0; i < arr.byteLength; i++) binary += String.fromCharCode(arr[i]);
                resolve(btoa(binary));
            }
        };
        reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
        reader.readAsDataURL(file);
    });

    const onPost = async () => {
        if (!description || description.trim() === '') {
            setDescriptionError('O campo de descrição está vazio. Por favor, escreva algo antes de postar.');
            return;
        }
        setPosting(true);
        try {
            // Build list of targets (one post per target). Rules:
            // - If fixedGroupObj present: only post to that group
            // - If publicOnly: always create a public post (GrupoId=null); if groups selected, also create per-group posts
            // - If onlyGroups: create one post per selected group
            // - Otherwise: if groups selected, post per group; else pick a default group as before

            const selectedArr = (!selectedGroup) ? [] : (Array.isArray(selectedGroup) ? selectedGroup : [selectedGroup]);
            const targets = [];

            if (fixedGroupObj) {
                targets.push({ GrupoId: getFixedGroupId(fixedGroupObj) });
            } else if (groupFlags && groupFlags.publicOnly) {
                // public + groups (if any) or only public
                targets.push({ GrupoId: null });
                if (selectedArr.length) {
                    selectedArr.forEach(s => targets.push({ GrupoId: s.Id || s.id || s.GrupoId || s.grupoId || null }));
                }
            } else if (groupFlags && groupFlags.onlyGroups) {
                if (!selectedArr.length) {
                    alert('Selecione um grupo antes de postar.');
                    setPosting(false);
                    return;
                }
                selectedArr.forEach(s => targets.push({ GrupoId: s.Id || s.id || s.GrupoId || s.grupoId || null }));
            } else {
                if (selectedArr.length) {
                    selectedArr.forEach(s => targets.push({ GrupoId: s.Id || s.id || s.GrupoId || s.grupoId || null }));
                } else {
                    // fallback: pick first available group as before
                    try {
                        const gdata = await apiCall('Grupo', 'GET');
                        const arr = Array.isArray(gdata) ? gdata : (gdata?.Grupos || gdata?.grupos || []);
                        if (arr && arr.length > 0) {
                            const first = arr[0];
                            const fallbackId = first.Id || first.id || first.GrupoId || first.grupoId || null;
                            targets.push({ GrupoId: fallbackId });
                        }
                    } catch (e) {
                        console.warn('Não foi possível obter grupos para selecionar default', e);
                    }
                    if (!targets.length) {
                        alert('Selecione um grupo antes de postar.');
                        setPosting(false);
                        return;
                    }
                }
            }

            // prepare description (include other file names)
            const otherFiles = (uploadFiles || []).filter(p => p.file && !(p.file.type && p.file.type.startsWith('image/')) && !((p.file.name||'').toLowerCase().endsWith('.pdf') || (p.file.type === 'application/pdf')));
            let finalDescription = description || '';
            if (otherFiles.length) {
                const names = otherFiles.map(p => p.file.name).filter(Boolean);
                if (names.length) finalDescription = finalDescription + '\n\nArquivos (não enviados como anexo): ' + names.join(', ');
            }

            const successes = [];
            const failures = [];

            // For each target, create a post and upload the same files
            for (const t of targets) {
                try {
                    const created = await apiCall('Postagem', 'POST', { Descricao: finalDescription || '', GrupoId: t.GrupoId });
                    const postId = created?.Id || created?.id || created?.PostagemId || created?.postagemId || null;

                    if (uploadFiles && uploadFiles.length > 0 && postId) {
                        let lastErr = null;
                        for (const p of uploadFiles) {
                            const f = p.file;
                            try {
                                const nameLower = (f.name || '').toLowerCase();
                                const isImage = f.type && f.type.startsWith('image/');
                                const isPdf = nameLower.endsWith('.pdf') || (f.type === 'application/pdf');

                                if (isImage) {
                                    const fd = new FormData();
                                    fd.append('file', f);
                                    fd.append('postagemId', postId);
                                    fd.append('tipoArquivo', f.type || '');
                                    fd.append('NomeArquivo', f.name || '');
                                    await apiUploadMultipart('PostagemArquivo', fd);
                                    continue;
                                }

                                if (isPdf) {
                                    const fd = new FormData();
                                    fd.append('file', f);
                                    fd.append('postagemId', postId);
                                    fd.append('tipoArquivo', f.type || 'application/pdf');
                                    fd.append('NomeArquivo', f.name || '');
                                    await apiUploadMultipart('PostagemArquivo', fd);
                                    continue;
                                }

                                // other file types already included in description
                                continue;
                            } catch (err) {
                                console.error('Erro ao processar upload de arquivo', err);
                                lastErr = err;
                            }
                        }
                        if (lastErr) throw lastErr;
                    }

                    // success for this target
                    const createdAtVal = created ? extractPostDate(created) : new Date().toISOString();
                    const toAdd = (postId) ? { id: postId, title, description: finalDescription, createdAt: createdAtVal, arquivos: uploadFiles.map(f=>f.file.name), __justCreated: Date.now() } : { id: Date.now(), title, description: finalDescription, createdAt: createdAtVal, arquivos: uploadFiles.map(f=>f.file.name), __justCreated: Date.now() };
                    onPosted && onPosted(toAdd);
                    successes.push(toAdd);
                } catch (err) {
                    console.error('Erro ao criar postagem ou enviar arquivos para target', t, err);
                    failures.push({ target: t, error: err });
                    // continue with next target
                }
            }

            if (successes.length) {
                try { uploadFiles.forEach(p => p.preview && URL.revokeObjectURL(p.preview)); } catch {}
                setUploadFiles([]); setTitle(''); setDescription(''); setDescriptionError(''); setExpanded(false);
                // reset UI: close emoji and groups, and reset selection/flags unless fixed group is enforced
                setEmojiOpen(false);
                setGroupsOpen(false);
                if (!fixedGroupObj) {
                    setSelectedGroup(null);
                    setGroupFlags({ publicOnly: true, onlyGroups: false });
                }
            }

            if (failures.length) {
                alert('Algumas postagens falharam: ' + failures.map(f => (f.target && (f.target.GrupoId == null ? 'Público' : `Grupo ${f.target.GrupoId}`)) + ' ').join(', '));
            }

        } catch (err) {
            console.error('Erro ao criar postagem ou enviar arquivos', err);
            alert('Erro ao enviar publicação: ' + (err.message || err));
        } finally { setPosting(false); }
    };

    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

const onCancel = () => { setExpanded(false); setTitle(''); setDescription(''); setDescriptionError(''); try { uploadFiles.forEach(p => p.preview && URL.revokeObjectURL(p.preview)); } catch {} setUploadFiles([]); };

// Show in-app confirmation modal instead of native confirm when there's content
const confirmAndCancel = () => {
    const hasContent = (title && title.trim() !== '') || (description && description.trim() !== '') || (uploadFiles && uploadFiles.length > 0);
    if (hasContent) {
        setShowCancelConfirm(true);
        return;
    }
    onCancel();
};

    const mediaAll = uploadFiles.filter(p => p.file && p.file.type && (p.file.type.startsWith('image/') || p.file.type.startsWith('video/')));

    return (
        <div className={`composer ${expanded ? 'expanded' : ''}`}>
            <LoadingOverlay isLoading={posting} message="Enviando publicações..." />
            <div className="input-row" style={{ display: expanded ? 'none' : 'block' }}>
                <input
                    className="short-input"
                    placeholder="O que você está pensando?"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onFocus={() => {
                        if (!autoExpand && window.innerWidth <= 768) {
                            // On mobile, open the popup instead of expanding inline
                            window.dispatchEvent(new Event('open-mobile-post-popup'));
                            return;
                        }
                        setExpanded(true);
                    }}
                />
            </div>
            {expanded && (
                <div className="composer-expanded">
                    {(() => {
                        const media = mediaAll;
                        if (!media || media.length === 0) return null;
                        const thumbsAll = media.slice(0, 9);
                        const remaining = Math.max(0, media.length - thumbsAll.length);
                        const isVideo = m => m.file && m.file.type && m.file.type.startsWith('video/');

                        const removeByKey = (file) => {
                            const key = `${file.name}_${file.size}`;
                            setUploadFiles(prev => {
                                prev.forEach(p => { if ((`${p.file.name}_${p.file.size}`) === key && p.preview) { try { URL.revokeObjectURL(p.preview); } catch {} } });
                                return prev.filter(p => `${p.file.name}_${p.file.size}` !== key);
                            });
                        };

                        // Parent container with three stacked children: tab (close), large display, small thumbnails
                        const first = media[0];
                        return (
                            <div className="images-container" style={{ marginBottom: 12 }}>
                                <div className="images-tab">
                                    <div style={{ flex: 1 }} />
                                    {first && (
                                        <button type="button" className="tab-close" title="Remover" aria-label="Remover" onClick={() => removeByKey(first.file)}>×</button>
                                    )}
                                </div>

                                <div className="images-large-container">
                                    {/* Show a larger preview of the first media if present (optional) */}
                                    {first && (isVideo(first) ? (
                                        <div className={`large-media-wrap ${first.isSquare ? 'square' : first.isPortrait ? 'portrait' : ''}`}><video src={first.preview} className={`large-media ${first.isSquare ? 'square' : first.isPortrait ? 'portrait' : ''}`} controls /></div>
                                    ) : (
                                        <div className={`large-media-wrap ${first.isSquare ? 'square' : first.isPortrait ? 'portrait' : ''}`}><img src={first.preview} alt={first.file.name} className={`large-media ${first.isSquare ? 'square' : first.isPortrait ? 'portrait' : ''}`} /></div>
                                    ))}
                                </div>

                                <div className="images-small-container">
                                    <div className="thumbnail-grid">
                                        {thumbsAll.map((t, idx) => {
                                            const isLast = idx === thumbsAll.length - 1;
                                            const tIsVideo = isVideo(t);
                                            return (
                                                <div key={`${t.file.name}_${t.file.size}`} className="thumb">
                                                    {tIsVideo ? <video src={t.preview} /> : <img src={t.preview} alt={t.file.name} />}
                                                    <button type="button" className="thumb-remove" title="Remover" aria-label="Remover miniatura" onClick={() => removeByKey(t.file)}>×</button>
                                                    {tIsVideo && <div className="thumb-video-icon">▶</div>}
                                                    {isLast && remaining > 0 && (
                                                        <div className="thumb-overlay" role="button" tabIndex={0} onClick={(e)=>{ e.stopPropagation(); setShowMediaGrid(true); }} onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowMediaGrid(true); } }}>
                                                            +{remaining}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                            {showMediaGrid && (
                                <div className="media-grid-modal-overlay" role="dialog" aria-modal="true" onClick={() => setShowMediaGrid(false)}>
                                    <div className="media-grid-modal" onClick={(e) => e.stopPropagation()}>
                                        <div className="media-grid-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div className="media-grid-title">Todas as fotos</div>
                                            <button className="modal-close-btn" onClick={() => setShowMediaGrid(false)} aria-label="Fechar">×</button>
                                        </div>
                                        <div className="media-grid" role="grid" style={{ marginTop: 8 }}>
                                            {uploadFiles.filter(p => p.file && p.file.type && p.file.type.startsWith('image/')).map((p) => (
                                                <div key={`${p.file.name}_${p.file.size}`} className="modal-thumb" role="gridcell">
                                                    {p.file.type && p.file.type.startsWith('video/') ? (
                                                        <video src={p.preview} className="modal-thumb-media" />
                                                    ) : (
                                                        <img src={p.preview} className="modal-thumb-media" alt={p.file.name} />
                                                    )}
                                                    <button className="thumb-remove" onClick={() => removeByKey(p.file)} aria-label="Remover foto">×</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            </div>
                        );
                    })()}

                    {(() => {
                        const files = uploadFiles.filter(p => p.file && !(p.file.type && (p.file.type.startsWith('image/') || p.file.type.startsWith('video/'))));
                        if (!files || files.length === 0) return null;
                        const fileEmoji = (name, type) => {
                            const low = (name || '').toLowerCase();
                            if (low.endsWith('.pdf') || (type && type === 'application/pdf')) return '📄';
                            if (low.endsWith('.txt') || (type && type === 'text/plain')) return '📝';
                            if (low.endsWith('.csv')) return '📑';
                            return '📎';
                        };

                        // helper to remove and revoke preview
                        const removeUpload = (file) => {
                            const key = `${file.name}_${file.size}`;
                            setUploadFiles(prev => {
                                prev.forEach(p => { if ((`${p.file.name}_${p.file.size}`) === key && p.preview) { try { URL.revokeObjectURL(p.preview); } catch {} } });
                                return prev.filter(p => `${p.file.name}_${p.file.size}` !== key);
                            });
                        };

                        // If more than 3 files, show 2 files + a +N button that opens a modal with all files
                        if (files.length > 3) {
                            const visible = files.slice(0, 2);
                            const moreCount = files.length - 2;
                            return (
                                <>
                                <div className="files-preview" style={{ marginBottom: 12 }}>
                                    <div className="files-row files-row-scrollable">
                                        {visible.map((f) => (
                                            <div key={`${f.file.name}_${f.file.size}`} className="file-item">
                                                <div className="file-title" title={f.file.name}>{f.file.name}</div>
                                                <button type="button" className="file-remove" title="Remover" aria-label="Remover arquivo" onClick={() => removeUpload(f.file)}>×</button>
                                            </div>
                                        ))}
                                        <div className="file-item file-more" role="button" tabIndex={0} onClick={() => setShowFilesModal(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowFilesModal(true); } }}>
                                            <div className="file-more-overlay">+{moreCount}</div>
                                        </div>
                                    </div>
                                </div>

                                {showFilesModal && (
                                    <div className="files-modal-overlay" role="dialog" aria-modal="true" onClick={() => setShowFilesModal(false)}>
                                        <div className="files-modal" onClick={(e) => e.stopPropagation()}>
                                            <div className="files-modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <div style={{ fontWeight: 700 }}>Arquivos anexados</div>
                                                <button className="modal-close-btn" onClick={() => setShowFilesModal(false)} aria-label="Fechar">×</button>
                                            </div>
                                            <div className="files-modal-list" style={{ marginTop: 8 }}>
                                                {files.map((f) => (
                                                    <div key={`${f.file.name}_${f.file.size}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px', borderBottom: '1px solid #eee' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <div style={{ fontSize: 18 }}>{fileEmoji(f.file.name, f.file.type)}</div>
                                                            <div style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</div>
                                                        </div>
                                                        <div>
                                                            <button className="file-remove" aria-label="Remover arquivo" onClick={() => removeUpload(f.file)}>×</button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                </>
                            );
                        }

                        // 3 or fewer files: show all inline
                        return (
                            <div className="files-preview" style={{ marginBottom: 12 }}>
                                <div className="files-row files-row-scrollable">
                                    {files.map((f, idx) => (
                                        <div key={`${f.file.name}_${f.file.size}`} className="file-item">
                                            <div className="file-title" title={f.file.name}>{f.file.name}</div>
                                            <button type="button" className="file-remove" title="Remover" aria-label="Remover arquivo" onClick={() => removeUpload(f.file)}>×</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Selected groups shown above the description */}
                    {/* Mode display: when onlyGroups show title + selected; when publicOnly show Publico badge */}
                    {groupFlags.onlyGroups && selectedGroup && (
                      <div className="groups-selected-top" style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span className="groups-public-inline" style={{ fontWeight: 700, marginRight: 8 }}>Somente grupos</span>
                              {Array.isArray(selectedGroup) ? selectedGroup.map(s => (
                                  <div key={(s.Id||s.id||s.GrupoId||s.nome||s.NomeGrupo)} className="groups-selected-badge-inline">
                                      <span className="groups-selected-name">{getGroupName(s)}</span>
                                      {!fixedGroupObj && <button type="button" className="groups-clear-btn" aria-label="Remover grupo" onClick={() => { setSelectedGroup(prev => { const next = prev.filter(x => (x.Id||x.id||x.GrupoId) !== (s.Id||s.id||s.GrupoId)); if (groupFlags.onlyGroups && (!next || next.length === 0)) setGroupFlags({ publicOnly: true, onlyGroups: false }); return next; }); }}>×</button>}
                                  </div>
                              )) : (
                                  <div className="groups-selected-badge-inline">
                                      <span className="groups-selected-name">{getGroupName(selectedGroup)}</span>
                                      {!fixedGroupObj && <button type="button" className="groups-clear-btn" aria-label="Remover grupo" onClick={() => { setSelectedGroup(null); if (groupFlags.onlyGroups) setGroupFlags({ publicOnly: true, onlyGroups: false }); }}>×</button>}
                                  </div>
                              )}
                          </div>
                      </div>
                    )}

                    {groupFlags.publicOnly && (
                        <div className="groups-selected-top" style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span className="groups-public-inline" style={{ fontWeight: 700, marginRight: 8 }}>Público</span>
                                {Array.isArray(selectedGroup) ? selectedGroup.map(s => (
                                    <div key={(s.Id||s.id||s.GrupoId||s.nome||s.NomeGrupo)} className="groups-selected-badge-inline">
                                      <span className="groups-selected-name">{getGroupName(s)}</span>
                                      {!fixedGroupObj && <button type="button" className="groups-clear-btn" aria-label="Remover grupo" onClick={() => setSelectedGroup(prev => prev.filter(x => (x.Id||x.id||x.GrupoId) !== (s.Id||s.id||s.GrupoId)))}>×</button>}
                                    </div>
                                )) : (selectedGroup && (
                                    <div className="groups-selected-badge-inline">
                                      <span className="groups-selected-name">{getGroupName(selectedGroup)}</span>
                                      {!fixedGroupObj && <button type="button" className="groups-clear-btn" aria-label="Remover grupo" onClick={() => setSelectedGroup(null)}>×</button>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {descriptionError && (
                        <div style={{ color: '#d32f2f', background: 'rgba(211,47,47,0.06)', padding: '8px', borderRadius: 6, marginBottom: 8, fontSize: 13 }} role="alert">{descriptionError}</div>
                    )}
                    <textarea
                        placeholder="Escreva aqui sua publicação, detalhes, links..."
                        value={description}
                        onChange={(e) => { const v = e.target.value; setDescription(v); if (descriptionError && v && v.trim()) setDescriptionError(''); }}
                    />

                    <div ref={emojiRef} style={{ position: 'relative' }}>
                        <div className="icon-row">
                            <button className="icon-btn" title="Adicionar foto" aria-label="Adicionar foto" onClick={onImageIconClick}>
                                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M21 19V7a2 2 0 0 0-2-2h-3.586l-.707-.707A.999.999 0 0 0 13.586 3H10.414a1 1 0 0 0-.707.293L9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/></svg>
                            </button>
                            <button className="icon-btn clip" title="Anexar arquivo (PDF/DOC/TXT)" aria-label="Anexar arquivo (somente PDF/DOC/TXT)" onClick={onFilesIconClick}>
                                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M21.44 11.05l-8.482 8.487a5.5 5.5 0 1 1-7.778-7.778L14.4 4.026a3.5 3.5 0 0 1 4.95 4.95l-9.192 9.198a1.5 1.5 0 0 1-2.122-2.122l8.484-8.489" stroke="currentColor" strokeWidth="0" fill="currentColor"/></svg>
                            </button>
                            <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImagesSelected} />

                            <input
                                ref={filesInputRef}
                                type="file"
                                multiple
                                accept=".pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                style={{ display: 'none' }}
                                onChange={handleFilesSelected}
                            />

                            <span style={{ position: 'relative' }}>
                                <button className="icon-btn emoji-container" title="Emoji" aria-label="Emoji" onClick={toggleEmoji}>
                                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" fill="currentColor" />
                                        <circle cx="9" cy="10" r="0.9" fill="#fff" />
                                        <circle cx="15" cy="10" r="0.9" fill="#fff" />
                                        <path d="M12 17.5c2.33 0 4.31-1.46 5.11-3.5h-10.22c.8 2.04 2.78 3.5 5.11 3.5z" fill="#fff" />
                                    </svg>
                                </button>
                                {emojiOpen && (
                                    <div className="emoji-dropdown" role="menu">
                                            <div className="emoji-tabs">
                                                {CATEGORIAS_EMOJI.map(cat => (
                                                    <button key={cat.chave} className={`emoji-tab ${emojiCategory === cat.chave ? 'active' : ''}`} onClick={() => setEmojiCategory(cat.chave)} title={cat.titulo} style={cat.bandeiras ? ESTILO_BANDEIRA : undefined}>{cat.emojis[0]}</button>
                                                ))}
                                            </div>
                                            <div className="emoji-grid" role="menu">
                                                {(CATEGORIAS_EMOJI.find(cat => cat.chave === emojiCategory) || CATEGORIAS_EMOJI[0]).emojis.map((em, i) => (
                                                    <button key={i} className="emoji-item" onClick={() => onEmojiSelect(em)} role="menuitem" title={em} style={emojiCategory === 'flags' ? ESTILO_BANDEIRA : undefined}>
                                                        {em}
                                                    </button>
                                                ))}
                                            </div>
                                    </div>
                                )}
                            </span>

                            {!hideGroupButton && !fixedGroupObj && (
                                <button type="button" className="groups-btn" title="+ Grupos" aria-label="+ Grupos" onClick={(e) => { e.stopPropagation(); setGroupsOpen(true); setEmojiOpen(false); }}>
                                    + Grupos
                                </button>
                            )}
                            {fixedGroupObj && (
                                <div className="groups-selected-badge-inline" style={{ marginLeft: 8 }}>
                                    <span className="groups-selected-name">{getFixedGroupName(fixedGroupObj)}</span>
                                </div>
                            )}

                            {expanded ? (
                                <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center' }}>
                                    {!hideCancelButton && <button onClick={confirmAndCancel} className="button-create" style={{ background: '#9e9e9e', marginLeft: 0 }}>Cancelar</button>}
                                    <button onClick={onPost} className="button-create" style={{ marginLeft: 0 }} disabled={posting}>{posting ? 'Enviando...' : 'Postar'}</button>
                                </div>
                            ) : (
                                <button className="button-create" style={{ marginLeft: 'auto' }} onClick={onCreateClick}>Criar publicação</button>
                            )}
                        </div>

                    </div>

                    {groupsOpen && (
                        <GroupsModal
                            onClose={() => setGroupsOpen(false)}
                            initialSelected={selectedGroup}
                            initialFlags={groupFlags}
                            onSelect={(sel, flags) => { setSelectedGroup(sel && sel.length ? sel : null); setGroupFlags(flags || { publicOnly: false, onlyGroups: false }); setGroupsOpen(false); }}
                            apiCall={apiCall}
                        />
                    )}

                    {showCancelConfirm && (
                        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setShowCancelConfirm(false)}>
                            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                                <div style={{ fontWeight: 700, marginBottom: 8 }}>Tem certeza que deseja cancelar?</div>
                                <div style={{ marginBottom: 16 }}>As alterações serão perdidas se você sair agora.</div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                    <button className="button-create" onClick={() => setShowCancelConfirm(false)}>Voltar</button>
                                    <button className="button-create" style={{ background: '#9e9e9e' }} onClick={() => { setShowCancelConfirm(false); onCancel(); }}>Sim, cancelar</button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            )}
        </div>
    );
}
